import { Component, computed, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { TranslocoPipe } from '@jsverse/transloco';
import { lastValueFrom } from 'rxjs';
import { SponsorExtended, SponsorPerson } from '../../../api/app-types/sponsor.app';
import { SPONSOR_SERVICE } from '../../../api/services/sponsor.service';
import { PaginatedResponse } from '../../../shared/pagination/paginated-response';
import { LvrHeading } from '../../../core/components/heading/heading';
import { LvrButton } from '../../../core/components/lvr-button/lvr-button';
import { NOTIFICATION_SERVICE } from '../../../shared/notification/notification';
import { createErrorNotification } from '../../../shared/notification/notification.factory';
import { NotificationService } from '../../../shared/notification/notification.service';
import { NewSponsorPersonDialog } from '../new-sponsor-person-dialog/new-sponsor-person-dialog';
import { SponsorPersonsList } from '../sponsor-persons-list/sponsor-persons-list';
import { SessionManagerService } from '../../../shared/storage/session-manager.service';

@Component({
	selector: 'lvr-sponsor-persons',
	standalone: true,
	templateUrl: './sponsor-persons.html',
	imports: [LvrHeading, TranslocoPipe, LvrButton, SponsorPersonsList],
	providers: [
		{
			provide: NOTIFICATION_SERVICE,
			useClass: NotificationService,
		},
	],
})
export class SponsorPersons {
	private readonly route = inject(ActivatedRoute);
	private readonly personsService = inject(SPONSOR_SERVICE);
	private readonly notificationService = inject(NOTIFICATION_SERVICE);
	private readonly dialog = inject(MatDialog);
	private readonly session = inject(SessionManagerService);
	private readonly data = toSignal(this.route.data);

	public readonly sponsor = computed(() => this.data()?.['sponsor'] as SponsorExtended);
	public readonly personsPage = signal<PaginatedResponse<SponsorPerson>>({
		entities: [],
		offset: 0,
		range: 0,
		totalEntities: 0,
	});
	private readonly initialPersonsPage = computed<PaginatedResponse<SponsorPerson>>(() => {
		const data = this.data();
		return data && 'sponsorPersons' in data
			? (data['sponsorPersons'] as PaginatedResponse<SponsorPerson>)
			: { entities: [], offset: 0, range: 0, totalEntities: 0 };
	});

	public constructor() {
		effect(() => {
			const page = this.initialPersonsPage();
			this.personsPage.set(page);
		});
	}

	public async onPageRequested(event: { pageIndex: number; pageSize: number }) {
		try {
			const offset = event.pageIndex;
			const range = event.pageSize;
			const updated = await this.personsService.getPersons(this.sponsor().id, offset, range);
			this.personsPage.set(updated);
		} catch {
			this.notificationService.addNotification(createErrorNotification('Fehler beim Neuladen der Ansprechpartner'));
		}
	}

	public async openNewSponsorPersonDialog() {
		const dialogRef = this.dialog.open(NewSponsorPersonDialog, {
			data: { sponsorId: this.sponsor().id },
			width: '40vw',
		});
		await lastValueFrom(dialogRef.afterClosed());
	}
}
